import React, {
    CSSProperties,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { observer } from 'mobx-react';
import classNames from 'classnames';

import Modal, {
    InitModalStyle,
    MODAL_PADDING_8_STEPS,
    MODAL_DISABLE_MIN_HEIGHT_CLASS,
    MODAL_ULTRA_HIGH_Z_INDEX,
} from '../../Common/Modal';
import Spinner from '../../Common/Spin';
import FileSavingIndicator from './FileSavingIndicator';
import LockModalContent from './LockSingaturesModalContent';
import { useMounted } from '../../hooks';
import {
    usePSPDFKitLifeCycle,
    useWatermarks,
    useFileSaving,
} from './hooks';
import { SimpleCallback } from '../../../types/types';
import { Instance, Size } from './pspdfkitTypes';
import {
    emailWatermarkPlugin,
    getToolbarItems,
    imageWatermarkPlugin,
    preventDefault,
} from './helpers';
import { LICENSE_KEY, CAROUSEL_MODE_STYLESHEET } from './constants';
import styles from './PDFViewer.module.scss';

const MODAL_STYLE: InitModalStyle = {
    additionalWrapClassName: classNames(
        MODAL_DISABLE_MIN_HEIGHT_CLASS,
        MODAL_PADDING_8_STEPS,
        MODAL_ULTRA_HIGH_Z_INDEX,
    ),
    initWidth: 400,
};

export interface PDFViewerPublicProps {
    setIsLoading: (value: boolean) => void;
    fileId: string;
    filePolicyId: string;
    isEditable: boolean;
    carouselMode?: boolean;
    PDFViewerClassName?: string;
    PDFViewerStyle?: CSSProperties;
    onSaveSignature?: SimpleCallback;
}

interface PDFViewerProps extends PDFViewerPublicProps {
    documentUrl: string;
}

const PDFViewer: FC<PDFViewerProps> = observer(({
    PDFViewerClassName,
    documentUrl,
    fileId,
    setIsLoading,
    filePolicyId,
    isEditable,
    PDFViewerStyle,
    carouselMode = false,
    onSaveSignature,
}) => {
    const [isLockModalOpen, setIsLockModalOpen] = useState<boolean>(false);
    const [isPDFLoading, setIsPDFLoading] = useState<boolean>(true);

    const containerRef = useRef<HTMLDivElement>();
    const isInitializedRef = useRef<boolean>(null);
    const PDFInstanceRef = useRef<Instance>(null);
    const savingDisposerRef = useRef<SimpleCallback>(null);

    const isMountedRef = useMounted();

    const {
        isSavingInProgress,
        hasError,
        hasUnFlattenAnnotations,
        connectInstance,
        saveFile,
        processUnFlattenAnnotations,
    } = useFileSaving(fileId, onSaveSignature);

    const watermarks = useWatermarks({
        filePolicyId,
        isMountedRef,
        setIsLoading,
    });

    const changeLifeCycleStage = usePSPDFKitLifeCycle();

    const onInstall = (): void => {
        changeLifeCycleStage('installed', () => setIsLoading(true));
        processUnFlattenAnnotations(PDFInstanceRef.current);
    };

    const onFirstRender = (): void => {
        changeLifeCycleStage('rendered', () => {
            setIsLoading(false);
            setIsPDFLoading(false);
        });
    };

    const handleInstance = (onInstallCallback?: SimpleCallback): void => {
        /*
        * readOnly to disable annotations/highlights
        * allowPrinting to enable/disable print:
        * not enough to hide print button, user can still Ctrl+P to print
        * */
        PDFInstanceRef.current?.setViewState((state) => state
            .set('readOnly', !isEditable)
            .set('allowPrinting', false)
            .set('allowExport', isEditable),
        );

        if (isEditable) {
            savingDisposerRef.current = connectInstance(PDFInstanceRef.current);
            PDFInstanceRef.current?.contentDocument.removeEventListener('contextmenu', preventDefault);
        } else {
            savingDisposerRef.current?.();
            PDFInstanceRef.current?.contentDocument.addEventListener('contextmenu', preventDefault);
        }

        PDFInstanceRef.current?.setToolbarItems((items) => {
            /*
            * Looks like pdfInstance doesn't have any specific hooks which being called on installation.
            * That's why we run side effect into setToolbarItems
            * */
            onInstallCallback?.();
            return getToolbarItems(items, isEditable);
        });
    };

    // Render PDF container after watermark is loaded or if options changed
    useEffect(() => {
        let cancelled = false;
        const container = containerRef.current;

        if (watermarks && !isInitializedRef.current) {
            setIsPDFLoading(true);
            (async function () {
                try {
                    if (PDFInstanceRef.current) {
                        await window.PSPDFKit?.unload(PDFInstanceRef.current);
                        PDFInstanceRef.current = null;
                    }

                    if (cancelled || !isMountedRef.current) {
                        return;
                    }

                    const waitForContainer = async (): Promise<boolean> => {
                        if (!container) return false;

                        if (container.clientHeight > 0) return true;

                        if (window.ResizeObserver) {
                            return new Promise<boolean>((resolve) => {
                                const observer = new ResizeObserver((entries) => {
                                    for (const entry of entries) {
                                        if (entry.contentRect.height > 0) {
                                            observer.disconnect();
                                            resolve(true);
                                            return;
                                        }
                                    }
                                });

                                observer.observe(container);

                                setTimeout(() => {
                                    observer.disconnect();
                                    resolve(false);
                                }, 2000);
                            });
                        }
                        await new Promise((resolve) => setTimeout(resolve, 100));
                        return container.clientHeight > 0;
                    };

                    const containerReady = await waitForContainer();
                    if (!containerReady || cancelled || !isMountedRef.current) {
                        console.warn('PDF container not ready, skipping load');
                        setIsLoading(false);
                        setIsPDFLoading(false);
                        return;
                    }

                    // Validate license key
                    if (!LICENSE_KEY) {
                        console.error('PSPDFKit license key is missing - evaluation mode will be used');
                    }

                    PDFInstanceRef.current = await window.PSPDFKit.load({
                        licenseKey: LICENSE_KEY,
                        preventTextCopy: !isEditable,
                        disableTextSelection: !isEditable,
                        styleSheets: carouselMode ? [CAROUSEL_MODE_STYLESHEET] : [],
                        container,
                        document: documentUrl,
                        baseUrl: `${window.location.protocol}//${window.location.host}/`,
                        renderPageCallback(
                            ctx: CanvasRenderingContext2D,
                            pageIndex: number,
                            pageSize: Size,
                        ) {
                            onFirstRender();
                            imageWatermarkPlugin(watermarks.logo, ctx, pageSize);
                            if (watermarks.email) {
                                emailWatermarkPlugin(watermarks.email, ctx, pageSize);
                            }
                        },
                        isAPStreamRendered: () => false,
                        renderPagePreview: false,
                        toolbarItems: [],
                        theme: window.PSPDFKit.Theme.AUTO,
                    });

                    if (cancelled || !isMountedRef.current) {
                        if (PDFInstanceRef.current) {
                            await window.PSPDFKit?.unload(PDFInstanceRef.current);
                            PDFInstanceRef.current = null;
                        }
                        return;
                    }

                    isInitializedRef.current = true;
                    handleInstance(onInstall);
                } catch (error) {
                    console.error('Failed to load PDF:', error);
                    if (!cancelled && isMountedRef.current) {
                        setIsLoading(false);
                        setIsPDFLoading(false);
                    }
                    isInitializedRef.current = false;
                }
            }());
        }

        return () => {
            cancelled = true;
        };
    }, [watermarks, documentUrl]);

    useEffect(() => () => {
        if (PDFInstanceRef.current) {
            window.PSPDFKit?.unload(PDFInstanceRef.current);
        }
        isInitializedRef.current = false;
        setIsLoading?.(false);
        setIsPDFLoading(false);
    }, []);

    useEffect(() => {
        isInitializedRef.current = false;
        setIsPDFLoading(true);
    }, [documentUrl]);

    useEffect(() => {
        if (PDFInstanceRef.current) {
            handleInstance();
        }
    }, [isEditable]);

    const memoLockChanges = useCallback(() => saveFile(true), []);

    const openLock = useCallback(() => setIsLockModalOpen(true), []);
    const closeLock = useCallback(() => setIsLockModalOpen(false), []);

    return (
        <div className={styles['outer-wrapper']}>
            <div
                ref={containerRef}
                className={classNames(styles.container, { [PDFViewerClassName]: !!PDFViewerClassName })}
                style={PDFViewerStyle}
            />
            {isEditable && (
                <>
                    <FileSavingIndicator
                        hasError={hasError}
                        isSavingInProgress={isSavingInProgress}
                        hasUnFlattenAnnotations={hasUnFlattenAnnotations}
                        onLockClick={openLock}
                        saveFile={memoLockChanges}
                        carouselMode={carouselMode}
                    />
                    <Modal
                        isOpen={isLockModalOpen}
                        closeModal={closeLock}
                        initStyle={MODAL_STYLE}
                        zIndex={9999}
                    >
                        <LockModalContent
                            close={closeLock}
                            lockSignatures={memoLockChanges}
                        />
                    </Modal>
                </>
            )}
        </div>
    );
});

export default PDFViewer;
